/**
 * Canvas helpers, carried over from the single-file demo unchanged in behaviour.
 *
 * Every figure in the platform is drawn rather than laid out, so each one needs
 * a backing store at the device's pixel ratio and a context already scaled to
 * CSS pixels. `hiCanvas` does both and hands back the logical width and height,
 * which is what the drawing code then works in.
 */

/** Retina backing store, capped at 3 — beyond that the memory cost buys nothing. */
export const HD = (): number => Math.min(window.devicePixelRatio || 1, 3);

export interface HiCanvas {
  g: CanvasRenderingContext2D;
  w: number;
  h: number;
}

/**
 * Size `c` to its CSS width and the given height, scale the context by the
 * device pixel ratio, and clear it. Returns the context and the logical size.
 *
 * Returns null where there is no 2D context to be had — jsdom under the tests,
 * and a browser that has run out of canvas contexts. Every drawing routine
 * checks, so a figure that cannot be drawn leaves an empty box rather than
 * taking the screen down with it.
 */
export function hiCanvas(c: HTMLCanvasElement, h: number): HiCanvas | null {
  const dpr = HD();
  const w = c.clientWidth || 600;
  c.width = w * dpr;
  c.height = h * dpr;
  c.style.height = h + "px";
  const g = c.getContext("2d");
  if (!g) return null;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, w, h);
  return { g, w, h };
}

/** The status colours. An operator learns these, so no theme moves them. */
export const GO: Record<string, string> = {
  go: "#3FD07A",
  caution: "#F2B03A",
  nogo: "#FF4D45",
  idle: "#5A7285",
  emerg: "#FFFFFF" /* the aerodrome emergency plan, activated */,
};

export const GOLAB: Record<string, string> = {
  go: "GO",
  caution: "CAUTION",
  nogo: "NO-GO",
  idle: "NO DATA",
};

export const worst = (arr: string[]): string =>
  arr.includes("nogo")
    ? "nogo"
    : arr.includes("caution")
      ? "caution"
      : arr.includes("go")
        ? "go"
        : "idle";
